// Auth Service - Single Responsibility: Manage authentication only
export type User = {
  id: number
  username: string
  email: string
  mfa_enabled?: boolean
  email_verified?: boolean
  created_at?: string
  avatar?: string
  preferredName?: string
}

export interface LoginRequest {
  username: string
  password: string
  rememberMe?: boolean
}

export interface LoginResponse {
  success: boolean
  user?: User
  user_id?: number
  mfa_required?: boolean
  approval_required?: boolean
  approval_status?: string
  message?: string
  error?: string
}

export class AuthService {
  private baseUrl = '/login'
  private currentUserUrl = '/api/current-user'

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
      redirect: 'manual' // Don't follow redirects automatically
    })

    // Handle redirects (302) - usually means already logged in or error
    if (response.status === 302 || response.status === 301 || response.type === 'opaqueredirect') {
      // Try to get current user to see if we're already logged in
      try {
        const userResponse = await this.getCurrentUser()
        if (userResponse.success && userResponse.user) {
          return {
            success: true,
            user: userResponse.user,
            message: 'Already logged in'
          }
        }
      } catch {
        // Not logged in, continue with error
      }
      throw new Error('Authentication failed')
    }

    if (!response.ok) {
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        const error = await response.json().catch(() => ({ error: 'Login failed' }))
        throw new Error(error.error || 'Login failed')
      }
      throw new Error(`Login failed: ${response.status} ${response.statusText}`)
    }

    const contentType = response.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Invalid response format from server')
    }

    const data: LoginResponse = await response.json()
    return data
  }

  async logout(): Promise<void> {
    // Hand the device's push token back BEFORE dropping the session.
    //
    // Defect found 2026-08-30: unregisterPushNotifications() existed but
    // nothing ever called it, so a token stayed bound to the account that
    // registered it. Signing a shared device into a second account left the
    // FIRST account still owning that device — a reminder for the old user
    // would ring on a device now being used by someone else. Seen for real:
    // an iPad signed in as one account, then re-signed as another, ended up
    // in push_tokens under BOTH.
    //
    // Best-effort: a failure here must never block signing out.
    try {
      const { unregisterPushNotifications, getCachedFcmToken } = await import('./pushService')
      const token = await getCachedFcmToken()
      await unregisterPushNotifications(token ?? undefined)
    } catch {
      /* never block logout on push cleanup */
    }

    const response = await fetch('/logout', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      redirect: 'manual'
    })

    if (!response.ok && response.status !== 302) {
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        const error = await response.json().catch(() => ({ error: 'Logout failed' }))
        throw new Error(error.error || 'Logout failed')
      }
      throw new Error(`Logout failed: ${response.status} ${response.statusText}`)
    }
  }

  async getCurrentUser(): Promise<LoginResponse> {
    const response = await fetch(this.currentUserUrl, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      redirect: 'manual'
    })

    // Handle redirects (302) - means not authenticated
    if (response.status === 302 || response.status === 301 || response.type === 'opaqueredirect') {
      return {
        success: false,
        error: 'Not authenticated'
      }
    }

    if (!response.ok) {
      return {
        success: false,
        error: `Failed to get current user: ${response.status} ${response.statusText}`
      }
    }

    const contentType = response.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      return {
        success: false,
        error: 'Invalid response format from server'
      }
    }

    const data: LoginResponse = await response.json()
    return data
  }

  async checkAuth(): Promise<boolean> {
    try {
      const response = await this.getCurrentUser()
      return response.success && !!response.user
    } catch {
      return false
    }
  }

  async register(registrationData: {
    username: string
    email: string
    password: string
    confirm_password: string
  }): Promise<LoginResponse> {
    // The browser knows the zone; the account will need it from day one and
    // nothing else in sign-up captures it. Server validates and only fills a
    // blank. See utils/signupTimezone.ts.
    const { detectedTimezone } = await import('../utils/signupTimezone')
    const response = await fetch('/register', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...registrationData, timezone: detectedTimezone() }),
      redirect: 'manual'
    })

    // Handle redirects (302) - usually means already logged in or error
    if (response.status === 302 || response.status === 301 || response.type === 'opaqueredirect') {
      throw new Error('Registration failed')
    }

    if (!response.ok) {
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        const error = await response.json().catch(() => ({ error: 'Registration failed' }))
        throw new Error(error.error || 'Registration failed')
      }
      throw new Error(`Registration failed: ${response.status} ${response.statusText}`)
    }

    const contentType = response.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Invalid response format from server')
    }

    const data: LoginResponse = await response.json()
    return data
  }
}

